import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useQueryClient } from "@tanstack/react-query"

import {
  getTransportInvalidationTargetOperations,
  getTransportOperationName,
  getTransportOperationsForCacheTags,
  getTransportOperationsForResource,
  isTransportInvalidationTarget,
  type TransportCacheTagInput,
  type TransportInvalidationTarget,
  type TransportOperationInput,
  type TransportOperationName,
  type TransportOperationResponse,
  type TransportResourceInput,
} from "@/types_api/backend/transport/operations"

import { executeTransportOperation } from "./transportOperationRuntime"
import {
  invalidateTransportOperationQueryKeys,
  notifyTransportQueryInvalidation,
} from "./transportQueryInvalidation"
import {
  getTransportInfiniteOperationQueryKey,
  getTransportInfiniteQueryKey,
  getTransportOperationQueryKey,
  getTransportPaginatedOperationQueryKey,
  getTransportPaginatedQueryKey,
  getTransportQueryKey,
  getTransportQueryOrganizationSlug,
  getTransportQueryWorkspaceId,
} from "./transportQueryKeys"
import type {
  TransportQueryKey,
  TransportResolvedRequest,
} from "./transportQueryTypes"
import { useTransportClient } from "./useTransportClient"

export function useTransportQueryUtils() {
  const queryClient = useQueryClient()
  const { getClient, organizationSlug, workspaceId } = useTransportClient()

  return useMemo(() => {
    const getQueryWorkspaceId = (operation: TransportOperationName) =>
      getTransportQueryWorkspaceId(operation, workspaceId)

    /** Invalidate every query for a resource/cache-tag target; request scoping is not used for target invalidation. */
    function invalidate(operation: TransportInvalidationTarget): Promise<void>
    /** Invalidate one operation; pass request to scope invalidation to a specific operation input. */
    function invalidate<Name extends TransportOperationName>(
      operation: TransportOperationInput<Name>,
      request?: TransportResolvedRequest<Name>,
    ): Promise<void>
    function invalidate<Name extends TransportOperationName>(
      operation: TransportInvalidationTarget | TransportOperationInput<Name>,
      request?: TransportResolvedRequest<Name>,
    ): Promise<void> {
      if (isTransportInvalidationTarget(operation)) {
        return invalidateTransportOperationQueryKeys(
          queryClient,
          organizationSlug,
          getTransportInvalidationTargetOperations(operation),
          workspaceId,
        )
      }

      const operationName = getTransportOperationName(operation)
      const queryOrganizationSlug = getTransportQueryOrganizationSlug(
        operationName,
        organizationSlug,
      )
      const queryWorkspaceId = getQueryWorkspaceId(operationName)
      const queryKey = getTransportQueryKey(
        operationName,
        request,
        queryOrganizationSlug,
        queryWorkspaceId,
      )
      notifyTransportQueryInvalidation(queryClient, {
        exact: true,
        operation: operationName,
        queryKey,
      })

      return Promise.all([
        queryClient.invalidateQueries({
          queryKey,
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: getTransportPaginatedQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          refetchType: "active",
        }),
        queryClient.invalidateQueries({
          queryKey: getTransportInfiniteQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          refetchType: "active",
        }),
      ]).then(() => undefined)
    }

    return {
      fetch<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        request?: TransportResolvedRequest<Name>,
      ): Promise<TransportOperationResponse<Name>> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return queryClient.fetchQuery({
          ...CACHE_PROFILES.standard,
          queryKey: getTransportQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          queryFn: () =>
            executeTransportOperation(getClient(), operationName, request),
        })
      },
      getKey<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        request?: TransportResolvedRequest<Name>,
      ): TransportQueryKey<Name> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return getTransportQueryKey(
          operationName,
          request,
          queryOrganizationSlug,
          queryWorkspaceId,
        )
      },
      invalidate,
      invalidateOperation<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
      ): Promise<void> {
        return invalidateTransportOperationQueryKeys(
          queryClient,
          organizationSlug,
          [getTransportOperationName(operation)],
          workspaceId,
        )
      },
      // Refetch every cached entry for an operation to completion, across all
      // request variants and inactive queries — for callers that must have
      // fresh data before navigating away, where invalidateOperation (active
      // observers only) wouldn't refetch an unmounted reader.
      refetchOperation<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
      ): Promise<void> {
        const operationName = getTransportOperationName(operation)
        return queryClient.refetchQueries({
          queryKey: getTransportOperationQueryKey(
            operationName,
            getTransportQueryOrganizationSlug(operationName, organizationSlug),
            getQueryWorkspaceId(operationName),
          ),
          type: "all",
        })
      },
      invalidateResource(resource: TransportResourceInput): Promise<void> {
        return invalidateTransportOperationQueryKeys(
          queryClient,
          organizationSlug,
          getTransportOperationsForResource(resource),
          workspaceId,
        )
      },
      invalidateCacheTags(
        cacheTags: readonly TransportCacheTagInput[],
      ): Promise<void> {
        return invalidateTransportOperationQueryKeys(
          queryClient,
          organizationSlug,
          getTransportOperationsForCacheTags(cacheTags),
          workspaceId,
        )
      },
      invalidatePaginated<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        request?: TransportResolvedRequest<Name>,
      ): Promise<void> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return Promise.all([
          queryClient.invalidateQueries({
            queryKey: getTransportPaginatedQueryKey(
              operationName,
              request,
              queryOrganizationSlug,
              queryWorkspaceId,
            ),
            refetchType: "active",
          }),
          queryClient.invalidateQueries({
            queryKey: getTransportInfiniteQueryKey(
              operationName,
              request,
              queryOrganizationSlug,
              queryWorkspaceId,
            ),
            refetchType: "active",
          }),
        ]).then(() => undefined)
      },
      /**
       * Marks a paginated operation stale and, by default, refetches whatever
       * is mounted.
       *
       * Pass `refetchType: "none"` when the caller has already written the
       * correct data into the cache. An infinite query refetches every page it
       * has loaded, one request at a time, so on a large list that refetch is
       * long enough to delay whatever the caller does next.
       */
      invalidatePaginatedOperation<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        options?: { refetchType?: "active" | "all" | "inactive" | "none" },
      ): Promise<void> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)
        const refetchType = options?.refetchType ?? "active"

        return Promise.all([
          queryClient.invalidateQueries({
            queryKey: getTransportPaginatedOperationQueryKey(
              operationName,
              queryOrganizationSlug,
              queryWorkspaceId,
            ),
            refetchType,
          }),
          queryClient.invalidateQueries({
            queryKey: getTransportInfiniteOperationQueryKey(
              operationName,
              queryOrganizationSlug,
              queryWorkspaceId,
            ),
            refetchType,
          }),
        ]).then(() => undefined)
      },
      updatePaginatedOperationData<Name extends TransportOperationName, TData>(
        operation: TransportOperationInput<Name>,
        updater: (data: TData | undefined) => TData | undefined,
      ): void {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        queryClient.setQueriesData<TData>(
          {
            queryKey: getTransportPaginatedOperationQueryKey(
              operationName,
              queryOrganizationSlug,
              queryWorkspaceId,
            ),
          },
          updater,
        )
      },
      invalidateInfinite<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        request?: TransportResolvedRequest<Name>,
      ): Promise<void> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return queryClient.invalidateQueries({
          queryKey: getTransportInfiniteQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          refetchType: "active",
        })
      },
      invalidateInfiniteOperation<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
      ): Promise<void> {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return queryClient.invalidateQueries({
          queryKey: getTransportInfiniteOperationQueryKey(
            operationName,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          refetchType: "active",
        })
      },
      setQueryData<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        data: TransportOperationResponse<Name>,
        request?: TransportResolvedRequest<Name>,
      ) {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        queryClient.setQueryData(
          getTransportQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
          data,
        )
      },
      getQueryData<Name extends TransportOperationName>(
        operation: TransportOperationInput<Name>,
        request?: TransportResolvedRequest<Name>,
      ) {
        const operationName = getTransportOperationName(operation)
        const queryOrganizationSlug = getTransportQueryOrganizationSlug(
          operationName,
          organizationSlug,
        )
        const queryWorkspaceId = getQueryWorkspaceId(operationName)

        return queryClient.getQueryData<TransportOperationResponse<Name>>(
          getTransportQueryKey(
            operationName,
            request,
            queryOrganizationSlug,
            queryWorkspaceId,
          ),
        )
      },
    }
  }, [getClient, organizationSlug, queryClient, workspaceId])
}
